import { Component } from '@theme/component';
class AshvenelleComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.busy = false;
    this.initJourney();
    this.initGallerySwipe();
    this.formController?.abort();
    this.formController = new AbortController();
    this.addEventListener('submit', this.addToCart, { signal: this.formController.signal });
  }
  disconnectedCallback() {
    this.gestureController?.abort();
    this.formController?.abort();
    this.controller?.abort();
    this.journeyObserver?.disconnect();
    cancelAnimationFrame(this.journeyFrame);
    super.disconnectedCallback();
  }
  initJourney() {
    this.journeyFrame = requestAnimationFrame(() => {
      this.journeyObserver?.disconnect();
      const stages = [...this.querySelectorAll('.av-journey-stage')];
      if (!stages.length) return;
      this.journeyObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting || entry.boundingClientRect.top < 0) entry.target.classList.add('is-lit');
          else entry.target.classList.remove('is-lit');
        }
      }, { rootMargin: '0px 0px -25% 0px', threshold: 0 });
      stages.forEach(stage => this.journeyObserver.observe(stage));
    });
  }
  initGallerySwipe() {
    this.gestureController?.abort();
    this.gestureController = new AbortController();
    const gallery = this.querySelector('.av-main');
    if (!gallery) return;
    const options = { passive:true, signal:this.gestureController.signal };
    let start = null;
    gallery.addEventListener('touchstart', event => {
      start = event.touches.length === 1 ? { x:event.touches[0].clientX, y:event.touches[0].clientY } : null;
    }, options);
    gallery.addEventListener('touchend', event => {
      if (!start || !matchMedia('(max-width:749px)').matches) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - start.x, dy = touch.clientY - start.y;
      start = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) this.moveImage(dx < 0 ? 1 : -1);
    }, options);
    gallery.addEventListener('touchcancel', () => { start = null; }, options);
  }
  previousVideo(event) { this.moveVideo(event, -1); }
  nextVideo(event) { this.moveVideo(event, 1); }
  moveVideo(event, direction) {
    const track = event.target.closest('.av-video-controls')?.previousElementSibling;
    if (!track?.firstElementChild) return;
    const step = track.firstElementChild.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
    const last = Math.max(0, track.scrollWidth - track.clientWidth);
    let target = Math.min(last, Math.max(0, track.scrollLeft + direction * step));
    if (direction > 0 && track.scrollLeft >= last - 2) target = 0;
    if (direction < 0 && track.scrollLeft <= 2) target = last;
    track.scrollTo({ left:target, behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  showImage(event) {
    const button = event.target.closest('button[data-image]');
    const image = this.querySelector('.av-main-photo');
    if (!button || !image) return;
    image.src = button.dataset.image;
    image.removeAttribute('srcset');
    image.alt = button.dataset.alt;
    for (const item of this.querySelectorAll('.av-thumbs button')) item.setAttribute('aria-pressed', String(item === button));
  }
  previousImage() { this.moveImage(-1); }
  nextImage() { this.moveImage(1); }
  moveImage(direction) {
    const buttons = [...this.querySelectorAll('.av-thumbs button')];
    const current = buttons.findIndex(button => button.getAttribute('aria-pressed') === 'true');
    const next = buttons[(current + direction + buttons.length) % buttons.length];
    next?.click();
    // Keep the page in place while changing the main photo.
    if (next) { const track = next.parentElement; track.scrollTo({ left:Math.max(0, next.offsetLeft - track.offsetLeft), behavior:'instant' }); }
  }
  addToCart = async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    if (this.busy) return;
    const button = event.submitter || form.querySelector('button[type="submit"]');
    if (!button || button.disabled) return;
    let errorBox = form.querySelector('[data-av-cart-error]');
    if (!errorBox) {
      errorBox = document.createElement('p');
      errorBox.dataset.avCartError = '';
      errorBox.setAttribute('role', 'alert');
      form.append(errorBox);
    }
    errorBox.textContent = '';
    this.busy = true;
    button.disabled = true;
    this.controller = new AbortController();
    let added = false;
    try {
      const data = new FormData(form);
      const item = { id:Number(data.get('id')), quantity:Number(data.get('quantity') || 1) };
      if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));
      if (item.selling_plan && item.quantity >= 2) item.properties = { 'Included gift':'Ashvenelle digital cookbook' };
      const response = await fetch(form.action + '.js', {
        method:'POST', headers:{ 'Content-Type':'application/json', Accept:'application/json' },
        body:JSON.stringify({ items:[item] }), signal:this.controller.signal
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add this item.');
      added = true;
      const refresh = await fetch('/?sections=cart-drawer-section', { signal:this.controller.signal });
      const sections = await refresh.json();
      const doc = new DOMParser().parseFromString(sections['cart-drawer-section'], 'text/html');
      const current = document.querySelector('#shopify-section-cart-drawer-section');
      const next = doc.querySelector('#shopify-section-cart-drawer-section');
      if (!current || !next) throw new Error();
      current.replaceWith(next);
      await customElements.whenDefined('theme-drawer');
      document.querySelector('cart-drawer-component')?.closest('theme-drawer')?.open();
    } catch (error) {
      if (error.name !== 'AbortError') errorBox.textContent = added
        ? 'Item added. Open your shopping bag to review it.'
        : (error.message || 'Unable to add this item. Please try again.');
    } finally {
      this.busy = false;
      button.disabled = false;
    }
  };
  selectOffer(event) {
    const input = event.target;
    if (!input.checked) return;
    this.querySelector('[data-price]').textContent = input.dataset.price;
    this.querySelector('[data-compare]').textContent = input.dataset.compare;
    this.querySelector('[data-button-price]').textContent = input.dataset.price;
    this.querySelector('[data-bundle-quantity]').value = input.dataset.quantity;
    this.querySelector('[data-bundle-terms]').textContent = input.dataset.terms;
  }
}
if (!customElements.get('ashvenelle-component')) customElements.define('ashvenelle-component', AshvenelleComponent);


